import type { TFunction } from 'i18next'
import type { Task } from '../db/models'
import { daysBetween, startOfDay } from '../date/calendar'
import { shortRecurrenceLabel } from './shortRecurrenceLabel'

type Successor = Pick<Task, 'startDate' | 'recurrence'>

/**
 * **„Kommt wieder"** — der Satz beim Abhaken einer wiederkehrenden Aufgabe. Ohne ihn stand
 * stumm eine zweite, gleichnamige Zeile da, und das las sich wie ein Duplikat (Plan
 * `recurring-page`, Schritt 6b). Morgen, dann bis sechs Tage der Wochentag, danach das Datum;
 * ein **Nachfolger** ohne Termin (Quote) nennt seinen Rhythmus. `null` = kein Nachfolger.
 */
export function comebackMessage(
  successors: readonly Successor[],
  t: TFunction,
  locale: string,
  today: Date,
): string | null {
  if (successors.length === 0) return null
  if (successors.length > 1) return t('recurrence.comeback.many', { count: successors.length })

  const { startDate, recurrence } = successors[0]!
  if (!startDate) {
    return recurrence
      ? t('recurrence.comeback.noDate', { rhythm: shortRecurrenceLabel(recurrence, t, locale) })
      : null
  }

  const days = daysBetween(startOfDay(today), startOfDay(startDate))
  if (days <= 0) return t('recurrence.comeback.today')
  if (days === 1) return t('recurrence.comeback.tomorrow')
  if (days <= 6) {
    return t('recurrence.comeback.weekday', {
      day: new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(startDate),
    })
  }
  const date = locale.startsWith('de')
    ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(startDate) // „20.09."
    : new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(startDate)
  return t('recurrence.comeback.date', { date })
}
